import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../Icon';
import { createRoom } from '../../api/rooms';
import {
  claimScheduledGameRoom,
  getScheduledGame,
  type ScheduledGame,
} from '../../api/scheduledGames';
import {
  formatCountdown,
  formatGameTime,
  googleCalendarUrl,
} from '../../lib/scheduledGameTime';

/**
 * §6.E — the card an invited friend sees when they open a host's
 * `?game=CODE` link. The first impression of the feature, so it's
 * gorgeous + on-brand: host name, theme, time, add-to-calendar, and a
 * state-aware primary CTA.
 *
 * <p>State logic (race-safe, reuses the existing room create/join — it
 * does NOT reimplement room logic):
 * <ul>
 *   <li><b>roomCode set OR joinableNow</b> → "Join now". If a {@code roomCode}
 *       is already claimed, join THAT via {@code onJoinByRoomCode}. Else (live
 *       but no room yet) {@code createRoom(private, themeId)} → claim it on the
 *       scheduled game. If the claim returns a DIFFERENT code (a friend beat us
 *       to it), join that one instead — idempotent + race-safe.
 *   <li><b>before joinable</b> → a friendly "Starts {when}" with a live
 *       countdown + add-to-calendar; polls {@link getScheduledGame} so the
 *       Join button lights up at game time without a refresh.
 * </ul>
 */
export interface ScheduledGameCardProps {
  /** The resolved scheduled game (from {@code getScheduledGame(code)}). */
  game: ScheduledGame;
  /** The viewer's player id; null while the anonymous player is still being
   *  created (the Join CTA waits on it). */
  playerId?: string | null;
  /** Reuse of the app's existing join-by-code path
   *  ({@code joinWheelRoomByCode}) — resolves a room invite code → room and
   *  drops the player into the wheel. */
  onJoinByRoomCode: (roomCode: string) => Promise<void>;
  /** Dismiss the card (also clears the pending game code). */
  onClose: () => void;
}

const POLL_MS = 20_000;

export function ScheduledGameCard({
  game: initialGame,
  playerId,
  onJoinByRoomCode,
  onClose,
}: ScheduledGameCardProps) {
  const [game, setGame] = useState<ScheduledGame>(initialGame);
  const [now, setNow] = useState<number>(() => Date.now());
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const joinFired = useRef(false);

  const joinable = game.joinableNow || !!game.roomCode || game.scheduledFor <= now;

  // Tick the local clock once a second so the countdown stays live (cheap; it
  // just re-renders this small card).
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  // Poll the server while we're waiting so the Join button activates at game
  // time (the server flips joinableNow / sets roomCode) without a refresh.
  // Stops once the game is joinable. Best-effort — a failed poll is ignored.
  useEffect(() => {
    if (joinable) return;
    const t = window.setInterval(() => {
      getScheduledGame(game.inviteCode)
        .then((fresh) => setGame(fresh))
        .catch(() => {
          /* transient — keep the last known state, the next tick retries */
        });
    }, POLL_MS);
    return () => window.clearInterval(t);
  }, [joinable, game.inviteCode]);

  /**
   * Enter the game. Race-safe room claim:
   *  1. If a room is already claimed, join it directly.
   *  2. Otherwise create a private room for this game's theme, claim it.
   *  3. If the claim returns a DIFFERENT code (someone beat us), join THAT.
   */
  const handleJoin = useCallback(async () => {
    if (joinFired.current || joining || !playerId) return;
    joinFired.current = true;
    setJoining(true);
    setError(null);
    try {
      // 1. Already-claimed room → straight in.
      if (game.roomCode) {
        await onJoinByRoomCode(game.roomCode);
        return;
      }
      // 2. No room yet — create one and claim it for this scheduled game.
      const room = await createRoom('wheel_of_fortune', game.themeId, true);
      const newCode = room.inviteCode;
      if (!newCode) {
        // Defensive — a private room should always carry a code.
        throw new Error('Room created without an invite code');
      }
      const { roomCode } = await claimScheduledGameRoom(
        game.inviteCode,
        playerId,
        newCode,
      );
      // 3. The claim is idempotent: if a friend claimed first, the server
      //    returns THEIR code, not ours. Join whichever the server blessed.
      await onJoinByRoomCode(roomCode);
    } catch (e) {
      console.warn('[scheduled-game] join failed', e);
      setError('Couldn’t join just yet. Give it a moment and try again.');
      joinFired.current = false;
      setJoining(false);
    }
  }, [game.inviteCode, game.roomCode, game.themeId, joining, onJoinByRoomCode, playerId]);

  const gcalUrl = googleCalendarUrl({
    title: game.title || `${game.hostName}’s Goodtime game`,
    startMs: game.scheduledFor,
    details: `Join the table: ${game.shareUrl}`,
  });

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="A friend invited you to a game"
      onClick={(e) => {
        if (e.target === e.currentTarget && !joining) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1001,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(8, 4, 22, 0.82)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        fontFamily: '"Fredoka", system-ui',
        padding: 18,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 380,
          borderRadius: 26,
          padding: '26px 22px 22px',
          textAlign: 'center',
          color: '#fff',
          overflow: 'hidden',
          background:
            'radial-gradient(ellipse 480px 300px at 50% -6%, rgba(167,139,250,0.4) 0%, transparent 60%), linear-gradient(180deg, #20103f 0%, #120726 100%)',
          border: '1.5px solid rgba(255,228,138,0.42)',
          boxShadow: '0 26px 64px rgba(0,0,0,0.66)',
        }}
      >
        {/* Festive glow ring behind the host badge */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: -70,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 220,
            height: 220,
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(255,196,12,0.25) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />

        <div
          aria-hidden
          style={{
            position: 'relative',
            margin: '0 auto 12px',
            width: 60,
            height: 60,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 28,
            background:
              'radial-gradient(circle at 35% 30%, #FFE57F, #FFC40C 60%, #B07F00)',
            boxShadow:
              '0 0 0 6px rgba(255,196,12,0.14), 0 8px 24px rgba(255,180,40,0.4)',
          }}
        >
          🎰
        </div>

        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 3,
            textTransform: 'uppercase',
            color: 'rgba(252,228,240,0.78)',
          }}
        >
          You’re invited
        </div>
        <h2
          style={{
            margin: '6px 0 2px',
            fontSize: 24,
            fontWeight: 900,
            color: '#ffe48a',
            textShadow: '0 2px 14px rgba(255,200,100,0.4)',
            lineHeight: 1.15,
          }}
        >
          {game.title ? game.title : `${game.hostName}’s table`}
        </h2>
        <p
          style={{
            margin: '0 0 16px',
            fontSize: 13,
            color: 'rgba(255,255,255,0.72)',
            fontWeight: 500,
          }}
        >
          <b style={{ color: '#fff' }}>{game.hostName}</b> invited you to spin
          the wheel together.
        </p>

        {/* When */}
        <div
          style={{
            padding: '12px 14px',
            borderRadius: 14,
            background: 'rgba(0,0,0,0.28)',
            border: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 800, color: '#fff' }}>
            {formatGameTime(game.scheduledFor)}
          </div>
          <div style={{ marginTop: 2, fontSize: 12, color: '#ffe48a', fontWeight: 700 }}>
            {joinable ? 'Live now — come on in!' : formatCountdown(game.scheduledFor, now)}
          </div>
        </div>

        {error ? (
          <div style={{ marginTop: 12, fontSize: 12, fontWeight: 600, color: '#ff9bb0' }}>
            {error}
          </div>
        ) : null}

        {/* Primary CTA */}
        {joinable ? (
          <button
            type="button"
            onClick={() => void handleJoin()}
            disabled={joining || !playerId}
            style={{
              marginTop: 16,
              width: '100%',
              padding: '15px 0',
              borderRadius: 999,
              border: '1.5px solid rgba(255,228,138,0.65)',
              background:
                joining || !playerId
                  ? 'rgba(255,255,255,0.1)'
                  : 'linear-gradient(180deg, #ff6bd6 0%, #d63dc4 100%)',
              color: '#fff',
              fontFamily: 'inherit',
              fontSize: 16,
              fontWeight: 900,
              letterSpacing: 0.5,
              cursor: joining || !playerId ? 'default' : 'pointer',
              opacity: joining || !playerId ? 0.7 : 1,
              boxShadow:
                joining || !playerId ? 'none' : '0 8px 24px rgba(214,61,196,0.45)',
            }}
          >
            {joining ? 'Joining…' : !playerId ? 'Getting ready…' : 'Join now'}
          </button>
        ) : (
          <div
            style={{
              marginTop: 16,
              padding: '13px 14px',
              borderRadius: 14,
              background: 'rgba(139,92,246,0.16)',
              border: '1px solid rgba(167,139,250,0.4)',
              fontSize: 13,
              fontWeight: 600,
              color: 'rgba(255,255,255,0.82)',
            }}
          >
            We’ll be here when it starts. Add it to your calendar so you don’t
            miss it.
          </div>
        )}

        {/* Add to calendar */}
        <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
          <a
            href={game.icsUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={calBtnStyle}
          >
            <Icon name="clock" size={14} alt="" />
            Add to calendar
          </a>
          <a
            href={gcalUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={calBtnStyle}
          >
            Google
          </a>
        </div>

        <button
          type="button"
          onClick={onClose}
          disabled={joining}
          style={{
            marginTop: 14,
            border: 'none',
            background: 'transparent',
            color: 'rgba(255,255,255,0.5)',
            fontFamily: 'inherit',
            fontSize: 12,
            fontWeight: 700,
            cursor: joining ? 'default' : 'pointer',
            opacity: joining ? 0.5 : 1,
          }}
        >
          {joinable ? 'Not now' : 'Maybe later'}
        </button>
      </div>
    </div>,
    document.body,
  );
}

const calBtnStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '10px 0',
  borderRadius: 999,
  border: '1px solid rgba(255,255,255,0.15)',
  background: 'rgba(255,255,255,0.05)',
  color: 'rgba(255,255,255,0.85)',
  fontFamily: '"Fredoka", system-ui',
  fontSize: 12,
  fontWeight: 700,
  textDecoration: 'none',
  cursor: 'pointer',
};
